/**
 * UserAttributesContext
 *
 * Fetches Cognito user attributes once after authentication and provides
 * them to the entire app via context. This eliminates the burst of
 * concurrent fetchUserAttributes() calls that caused TooManyRequestsException.
 *
 * Usage:
 *   const { attributes, loading, refresh } = useUserAttributes();
 *   const isPro = attributes?.['custom:subscription_status'] === 'active';
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { invalidateUserAttributesCache } from '../utils/userAttributesCache';

export type UserAttributes = Record<string, string | undefined>;

interface UserAttributesContextValue {
  attributes: UserAttributes | null;
  loading: boolean;
  error: string | null;
  /** Force a fresh fetch — call after updating user attributes */
  refresh: () => Promise<void>;
}

const UserAttributesContext = createContext<UserAttributesContextValue>({
  attributes: null,
  loading: true,
  error: null,
  refresh: async () => {},
});

interface UserAttributesProviderProps {
  children: ReactNode;
}

export function UserAttributesProvider({ children }: UserAttributesProviderProps) {
  const [attributes, setAttributes] = useState<UserAttributes | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAttributes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const attrs = await fetchUserAttributes();
      setAttributes(attrs as UserAttributes);
      // Keep the module-level cache in sync for any legacy callers
      // that haven't been migrated to context yet
      invalidateUserAttributesCache();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user attributes');
      setAttributes(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAttributes();
  }, [fetchAttributes]);

  const refresh = useCallback(async () => {
    invalidateUserAttributesCache();
    await fetchAttributes();
  }, [fetchAttributes]);

  return (
    <UserAttributesContext.Provider value={{ attributes, loading, error, refresh }}>
      {children}
    </UserAttributesContext.Provider>
  );
}

export function useUserAttributes(): UserAttributesContextValue {
  return useContext(UserAttributesContext);
}
